import { useCallback, useEffect, useRef, useState } from 'react'
import { Canvas } from '@react-three/fiber'
import * as THREE from 'three'
import { Foutgrens } from './Foutgrens'
import { ProductScene } from './ProductScene'
import type { SceneHandvat } from './ProductScene'
import { laadAlvast } from './laden'
import { Draaitafel } from './Draaitafel'
import type { ProductConfig } from './types'
import { ProductsheetQR } from './ProductsheetQR'
import './viewer.css'

type Taal = 'nl' | 'en'

const TEKST = {
  laden: { nl: 'Model laden…', en: 'Loading model…' },
  herstel: { nl: 'Beginstand', en: 'Reset view' },
  sluit: { nl: 'Sluiten', en: 'Close' },
  draai: { nl: 'Sleep om te draaien · knijp of scroll om te zoomen', en: 'Drag to rotate · pinch or scroll to zoom' },
  draaitafel: { nl: 'Sleep om de machine rond te draaien', en: 'Drag to turn the machine around' },
  geenModel: {
    nl: 'Van deze machine tonen we voorlopig de foto.',
    en: 'For now we are showing a photo of this machine.',
  },
  geenModelSub: {
    nl: 'Vraag onze collega\u2019s op de stand naar een demonstratie.',
    en: 'Ask our colleagues at the stand for a demonstration.',
  },
  fout: { nl: 'Van deze machine tonen we de foto.', en: 'We are showing a photo of this machine.' },
  foutSub: {
    nl: 'Vraag onze collega\u2019s op de stand naar een demonstratie.',
    en: 'Ask our colleagues at the stand for a demonstration.',
  },
}

/*
 * Wat de ontwikkelaar moet weten hoort in de console, niet op het paneel.
 * Hier stond de instructie om model.glb in /public/products/ te zetten — leesbaar
 * voor iedere bezoeker die op een machine zonder model klikt. De bezoeker krijgt
 * nu een zin die klopt in de situatie waarin hij hem leest; de aanwijzing komt
 * terecht bij wie er iets mee kan.
 */
const meldOntbrekendModel = (product: ProductConfig, fout: boolean) => {
  if (fout) {
    console.warn(
      `[productviewer] Model van "${product.naam}" (${product.id}) kon niet geladen worden: ${product.modelUrl}. ` +
        'De preview blijft staan. Zie docs/3d-assets.md.',
    )
    return
  }
  console.warn(
    `[productviewer] Geen 3D-model voor "${product.naam}" (${product.id}). ` +
      `Plaats model.glb in /public/products/${product.id}/ en zet modelUrl in de catalogus. Zie docs/3d-assets.md.`,
  )
}

const t = (k: keyof typeof TEKST, taal: Taal) => TEKST[k][taal]

/**
 * Eén viewer voor alle producten.
 *
 * Er zit bewust geen productkennis in dit bestand. Alles wat per machine
 * verschilt komt uit de `ProductConfig`; de viewer meet de rest zelf op. Een
 * elfde product toevoegen raakt deze code dus niet.
 *
 * De opbouw is drielaags, en die volgorde is de foutafhandeling:
 *
 * 1. de **preview-afbeelding** staat er altijd, meteen, als achtergrond;
 * 2. daarboven de **canvas** die inschuift zodra het model geladen is;
 * 3. mislukt dat, dan verdwijnt laag 2 en blijft laag 1 staan met een melding.
 *
 * Er is dus geen toestand waarin de bezoeker naar een leeg vlak kijkt.
 */
export function Product3DViewer({
  product, taal = 'nl', onSluiten, volgendProduct, klasse, stilstaand = false,
}: {
  product: ProductConfig
  taal?: Taal
  /** Zonder dit wordt de sluitknop niet getoond (bijvoorbeeld op een eigen pagina). */
  onSluiten?: () => void
  /** Wordt stil voorgeladen zodra dit product staat. */
  volgendProduct?: ProductConfig
  klasse?: string
  /**
   * Houdt de tekenbuffer vast en zet het draaien stil. Nodig om een beeld uit de
   * canvas te kunnen lezen — zonder dit levert elke schermafdruk zwart op, want
   * WebGL gooit de buffer na elk frame weg. Alleen aanzetten om te controleren:
   * het kost geheugen en een extra kopie per frame.
   */
  stilstaand?: boolean
}) {
  const [geladen, zetGeladen] = useState(false)
  const [fout, zetFout] = useState(false)
  const handvat = useRef<SceneHandvat | null>(null)
  const heeftModel = Boolean(product.modelUrl)
  // Zonder model, met draaitafel: de renders van de fabrikant. Mislukt het
  // model, dan valt hij daar ook op terug voordat hij de poster toont.
  const toontDraaitafel = Boolean(product.draaitafel) && (!heeftModel || fout)

  // Pas het volgende product voorladen als dit er staat — anders vechten twee
  // downloads om dezelfde bandbreedte en duurt juist het zichtbare model langer.
  useEffect(() => {
    if (!geladen || !volgendProduct) return
    const id = window.setTimeout(() => laadAlvast(volgendProduct), 1200)
    return () => window.clearTimeout(id)
  }, [geladen, volgendProduct])

  useEffect(() => {
    zetGeladen(false)
    zetFout(false)
  }, [product.id])

  // Eén melding per product per toestand, in de console waar ze thuishoort.
  useEffect(() => {
    if ((!heeftModel || fout) && !product.draaitafel) meldOntbrekendModel(product, fout)
  }, [product, heeftModel, fout])

  const pakHandvat = useCallback((h: SceneHandvat) => { handvat.current = h }, [])
  const meldGeladen = useCallback(() => zetGeladen(true), [])

  return (
    <div className={`pv${klasse ? ` ${klasse}` : ''}`}>
      {/* Laag 1: de foto. Staat er altijd en als eerste. */}
      <img
        className={`pv-poster${geladen ? ' pv-weg' : ''}`}
        src={product.previewUrl}
        alt={product.naam}
        decoding="async"
      />

      {/* Laag 2: het model. */}
      {heeftModel && !fout && (
        <div className={`pv-canvas${geladen ? ' pv-aan' : ''}`}>
          <Foutgrens
            onFout={() => zetFout(true)}
            terugval={null}
            kind={
              <Canvas
                shadows
                dpr={[1, 2]}
                gl={{
                  antialias: false,
                  alpha: false,
                  powerPreference: 'high-performance',
                  toneMapping: THREE.ACESFilmicToneMapping,
                  toneMappingExposure: product.belichting,
                  preserveDrawingBuffer: stilstaand,
                }}
                camera={{ fov: 32, near: 0.05, far: 200 }}
              >
                <ProductScene
                  product={product as ProductConfig & { modelUrl: string }}
                  draait={!stilstaand}
                  onHandvat={pakHandvat}
                  onGeladen={meldGeladen}
                />
              </Canvas>
            }
          />
        </div>
      )}

      {/* Laag 2b: de draaitafel, als er geen model is. */}
      {toontDraaitafel && (
        <Draaitafel key={product.id} config={product.draaitafel!} onGeladen={meldGeladen} draait={!stilstaand} />
      )}

      {/* Laag 3: de tekstlaag. */}
      <div className="pv-ui">
        <div className="pv-kop">
          <div>
            <h3 className="pv-naam">{product.naam}</h3>
            {product.omschrijving && <p className="pv-sub">{product.omschrijving[taal]}</p>}
          </div>
          {onSluiten && (
            <button className="pv-knop pv-sluit" onClick={onSluiten} aria-label={t('sluit', taal)}>
              ✕
            </button>
          )}
        </div>

        {/* De productsheet rechtsonder in de viewer, boven de voet: wie de
            machine van dichtbij bekijkt, neemt de sheet mee op zijn telefoon. */}
        {product.sheet && (
          <div className="pv-sheet">
            <ProductsheetQR sheet={product.sheet} taal={taal} />
          </div>
        )}

        <div className="pv-voet">
          {heeftModel && !fout && geladen && (
            <>
              <span className="pv-hint">{t('draai', taal)}</span>
              <button className="pv-knop" onClick={() => handvat.current?.herstel()}>
                {t('herstel', taal)}
              </button>
            </>
          )}
          {heeftModel && !fout && !geladen && <span className="pv-hint pv-puls">{t('laden', taal)}</span>}
          {toontDraaitafel && (
            <span className={`pv-hint${geladen ? '' : ' pv-puls'}`}>{t(geladen ? 'draaitafel' : 'laden', taal)}</span>
          )}
        </div>

        {(!heeftModel || fout) && !toontDraaitafel && (
          <div className="pv-melding">
            <strong>{t(fout ? 'fout' : 'geenModel', taal)}</strong>
            <span>{t(fout ? 'foutSub' : 'geenModelSub', taal)}</span>
          </div>
        )}
      </div>
    </div>
  )
}
